import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Boxes, Home, Route, Droplet, Waves, Building2, PlugZap, HardHat, Ruler,
  Wrench, Calculator, Landmark, CheckCircle2, ArrowRight, Lock, Pencil,
  Video, Image as ImageIcon, Download, Monitor, Smartphone, X,
} from "lucide-react";
import { COREX_ENTRY } from "../../lib/constants";
import { VideoModal } from "../VideoModal";
import { MediaAdminModal } from "../MediaAdminModal";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

const RELEASE_LABEL = {
  draft: "En preparación", available: "Disponible", updating: "Actualizando",
  maintenance: "Mantenimiento", coming_soon: "Próximamente",
};
function fmtSize(b) {
  if (!b || b <= 0) return null;
  const mb = b / 1048576;
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`;
}

const ICONS = {
  Boxes, Home, Route, Droplet, Waves, Building2, PlugZap, HardHat, Ruler,
  Wrench, Calculator, Landmark,
};

const ESTADO_META = {
  DISPONIBLE: { txt: "Activo", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  BETA: { txt: "Beta", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  PROXIMAMENTE: { txt: "Próximamente", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  NO_DISPONIBLE: { txt: "No disponible", cls: "bg-slate-100 text-slate-500 border-slate-200" },
};

const ACCENT = "#16A34A";

export const ProgramaCard = ({ programa, isAdmin, onEdit, onMediaSaved, className = "" }) => {
  const navigate = useNavigate();
  const [videoOpen, setVideoOpen] = useState(false);
  const [media, setMedia] = useState(null); // "video" | "imagen"
  const [dlOpen, setDlOpen] = useState(false);
  const isMaster = programa.type === "MASTER_PROGRAM";
  const Icon = ICONS[programa.icon] || Boxes;
  const estado = ESTADO_META[programa.status] || ESTADO_META.PROXIMAMENTE;
  // Programa especializado con módulo interno activo (Centro de Diseño): Vivienda Multifamiliar.
  const internalRoute = (() => {
    // PASO 3: ruteo por SLUG EXACTO. Solo los programas legacy con motor web abren su Centro
    // de Diseño; los programas Desktop (p. ej. corex-multifamiliar) NO reutilizan el motor web.
    const slug = (programa.slug || "").toLowerCase();
    if (slug === "corex-valorizaciones") return "/software/corex-valorizaciones";
    if (slug === "vivienda-multifamiliar") return "/software/vivienda-multifamiliar";
    return null;
  })();
  const habilitado = isMaster
    ? true
    : internalRoute
      ? true
      : (programa.status === "DISPONIBLE" || programa.status === "BETA") && !!programa.app_url;
  const abrirLabel = isMaster ? `Abrir ${programa.name}` : internalRoute ? "Abrir Centro de Diseño" : "Abrir módulo";
  // Solo VISUAL: la tarjeta de Vivienda Multifamiliar muestra únicamente el logo COREX Multifamiliar + botón (no cambia rutas/lógica).
  const isMultifamiliar = !!internalRoute && /vivienda|multifamiliar/.test(`${programa.slug || ""} ${programa.name || ""}`.toLowerCase());
  const MULTIFAMILIAR_LOGO = "/corex-multifamiliar-logo.png";
  const openLabel = isMultifamiliar ? "Abrir Programa Multifamiliar" : abrirLabel;
  // F4-C corr.: imagen universal. Si hay imagen (archivo o URL), la tarjeta muestra SOLO la
  // imagen (object-contain + padding), ocultando texto/ícono/descripción, con el mismo contenedor
  // plantillado por COREX Multifamiliar.
  const imgSrc = programa.tiene_imagen_archivo
    ? `${BACKEND}/api/software/programas/${programa.id}/imagen`
    : (programa.image_url || null);
  const hasImage = !!imgSrc;

  const handleOpen = () => {
    if (isMaster) {
      navigate(COREX_ENTRY);
      return;
    }
    if (internalRoute) {
      navigate(internalRoute);
      return;
    }
    if (habilitado) window.open(programa.app_url, "_blank", "noopener,noreferrer");
  };

  // PASO 2B · Distribución multiplataforma (windows + android). UNA tarjeta, UNA licencia.
  const dists = programa.distributions || {};
  const buildPlat = (key) => {
    const d = dists[key] || {};
    const ext = d.distribution_type === "external_url" && !!d.download_url;
    const upl = d.distribution_type === "uploaded_file" && !!d.tiene_instalador;
    const available = !!d.enabled && (ext || upl) && d.release_status === "available";
    const href = ext
      ? d.download_url
      : (upl ? `${BACKEND}/api/software/programas/${programa.id}/installer/${key}` : null);
    return {
      enabled: !!d.enabled, available, href, isUpload: upl,
      version: d.version, architecture: d.architecture, file_size: d.file_size,
      release_status: d.release_status,
    };
  };
  const platforms = [
    { key: "windows", label: "Windows", Icon: Monitor, ...buildPlat("windows") },
    { key: "android", label: "Android", Icon: Smartphone, ...buildPlat("android") },
  ];
  const anyDownloadable = platforms.some((p) => p.available);
  const enabledPlats = platforms.filter((p) => p.enabled);
  const precios = programa.precios || null;
  const minCent = precios
    ? Math.min(...["m1", "m3", "m6", "m12"].map((k) => Number(precios[k]) || 0).filter((v) => v > 0))
    : null;

  return (
    <div
      data-testid={`programa-card-${programa.id}`}
      className={`relative flex flex-col rounded-2xl bg-white p-5 transition-shadow duration-200 hover:shadow-lg focus-within:shadow-lg ${
        isMaster
          ? "border-2 shadow-md md:p-6"
          : "border border-slate-200 shadow-soft"
      } ${className}`}
      style={isMaster ? { borderColor: ACCENT, background: "linear-gradient(160deg,#FFFFFF 70%, rgba(22,163,74,0.06) 100%)" } : undefined}
    >
      {/* Badge etiqueta (Programa Maestro) */}
      {isMaster && programa.label && (
        <span
          data-testid={`programa-label-${programa.id}`}
          className="absolute -top-2.5 left-5 inline-flex items-center rounded-full px-3 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white shadow-sm"
          style={{ backgroundColor: ACCENT }}
        >
          {programa.label}
        </span>
      )}

      {/* Grupo de acciones admin (F4-C): [ Video ] [ Imagen ] [ Editar ] · mismo lenguaje visual */}
      {isAdmin ? (
        <div className="absolute right-3 top-3 z-10 flex items-center gap-1.5" data-testid={`programa-acciones-${programa.id}`}>
          <button
            type="button"
            onClick={() => setMedia("video")}
            data-testid={`programa-video-admin-${programa.id}`}
            aria-label={`Video de ${programa.name}`}
            className="rounded-lg border border-slate-200 bg-white p-1.5 text-slate-500 hover:bg-slate-50 hover:text-slate-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-blue"
          >
            <Video size={14} />
          </button>
          {(
            <button
              type="button"
              onClick={() => setMedia("imagen")}
              data-testid={`programa-imagen-admin-${programa.id}`}
              aria-label={`Imagen de ${programa.name}`}
              className="rounded-lg border border-slate-200 bg-white p-1.5 text-slate-500 hover:bg-slate-50 hover:text-slate-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-blue"
            >
              <ImageIcon size={14} />
            </button>
          )}
          <button
            type="button"
            onClick={() => onEdit(programa)}
            data-testid={`programa-editar-${programa.id}`}
            aria-label={`Editar ${programa.name}`}
            className="rounded-lg border border-slate-200 bg-white p-1.5 text-slate-500 hover:bg-slate-50 hover:text-slate-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-blue"
          >
            <Pencil size={14} />
          </button>
        </div>
      ) : (
        programa.tiene_video_demo && (
          <button
            type="button"
            onClick={() => setVideoOpen(true)}
            data-testid={`programa-video-${programa.id}`}
            aria-label={`Ver video de ${programa.name}`}
            className="absolute right-3 top-3 z-10 rounded-lg border border-slate-200 bg-white p-1.5 text-slate-500 hover:bg-slate-50 hover:text-brand-blue focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-blue"
          >
            <Video size={14} />
          </button>
        )
      )}

      {/* Franja superior: badge de estado a la izquierda (deja libre la esquina de los íconos) */}
      <div className="flex items-center gap-2 mb-3 pr-[7rem] min-h-[1.75rem]">
        <span
          data-testid={`programa-estado-${programa.id}`}
          className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${estado.cls}`}
        >
          {estado.txt}
        </span>
      </div>

      {(hasImage || isMultifamiliar) ? (
        /* Imagen/logo a ancho completo del cuerpo · mismo patrón para preexistente y subida (indistinguible) */
        <div
          className="flex flex-1 items-center justify-center py-2"
          data-testid={hasImage ? `programa-imagen-${programa.id}` : `programa-logo-${programa.id}`}
        >
          <img
            src={hasImage ? imgSrc : MULTIFAMILIAR_LOGO}
            alt={hasImage ? programa.name : "COREX Multifamiliar"}
            className="w-full h-auto max-h-72 object-contain select-none"
            draggable="false"
            loading="lazy"
          />
        </div>
      ) : (
        <>
      <div className="flex items-start gap-3">
        <div
          className={`flex items-center justify-center rounded-xl ${isMaster ? "h-14 w-14" : "h-11 w-11"}`}
          style={{ backgroundColor: isMaster ? "rgba(22,163,74,0.12)" : "#F1F5F9", color: isMaster ? ACCENT : "#334155" }}
          aria-hidden="true"
        >
          <Icon size={isMaster ? 30 : 24} />
        </div>
      </div>

      <h3 className={`mt-3 font-extrabold text-slate-900 ${isMaster ? "text-xl" : "text-base"}`}>
        {programa.name}
      </h3>
      {isMaster ? (
        <p className="mt-1 text-xs font-semibold" style={{ color: ACCENT }}>
          Programa Maestro de Ingeniería
        </p>
      ) : (
        <p className="mt-1 text-xs font-semibold text-slate-400">Software especializado</p>
      )}

      <p className="mt-2 text-sm leading-relaxed text-slate-600">
        {isMaster ? programa.functional_desc || programa.description : programa.description}
      </p>

      {/* Capacidades */}
      {programa.capabilities?.length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {programa.capabilities.map((cap) => (
            <li key={cap} className="flex items-start gap-2 text-[13px] text-slate-600">
              <CheckCircle2 size={15} className="mt-0.5 shrink-0" style={{ color: isMaster ? ACCENT : "#64748B" }} aria-hidden="true" />
              <span>{cap}</span>
            </li>
          ))}
        </ul>
      )}
        </>
      )}

      <div className="mt-auto pt-5">
        {(enabledPlats.length > 0 || programa.licenciable) && (
          <div className="mb-3 space-y-1" data-testid={`programa-dist-${programa.id}`}>
            {enabledPlats.map((p) => (
              <div
                key={p.key}
                data-testid={`programa-dist-${p.key}-${programa.id}`}
                className={`flex items-center gap-1.5 text-[11px] ${p.available ? "text-emerald-700" : "text-slate-400"}`}
              >
                <p.Icon size={12} aria-hidden="true" />
                <span className="font-semibold">{p.label}</span>
                {p.version && <span>· v{p.version}</span>}
                <span className="font-semibold uppercase tracking-wide">· {RELEASE_LABEL[p.release_status] || "—"}</span>
              </div>
            ))}
            {programa.licenciable && (
              <div className="pt-0.5 text-[11px] font-semibold text-slate-600" data-testid={`programa-licencia-info-${programa.id}`}>
                {minCent ? `Licencias desde S/ ${(minCent / 100).toFixed(0)}` : "Programa con licencia"} · Máx {programa.max_devices || 1} disp.
              </div>
            )}
          </div>
        )}
        {anyDownloadable && (
          <button
            type="button"
            onClick={() => setDlOpen(true)}
            data-testid={`programa-descargar-${programa.id}`}
            className="mb-2 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-emerald-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-700"
          >
            <Download size={16} aria-hidden="true" /> Descargar
          </button>
        )}
        <button
          type="button"
          onClick={handleOpen}
          disabled={!habilitado}
          aria-disabled={!habilitado}
          data-testid={`programa-abrir-${programa.id}`}
          title={habilitado ? openLabel : "Disponible próximamente"}
          className={`inline-flex w-full items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-blue ${
            habilitado
              ? isMultifamiliar
                ? "text-white shadow-md hover:brightness-110"
                : isMaster
                  ? "text-white shadow-sm hover:brightness-110"
                  : "border border-slate-200 text-slate-700 hover:bg-slate-50"
              : "cursor-not-allowed border border-slate-200 bg-slate-50 text-slate-400"
          }`}
          style={
            habilitado && isMultifamiliar
              ? { background: "linear-gradient(135deg,#ffb072 0%,#f97316 42%,#ea580c 100%)", boxShadow: "0 6px 18px rgba(234,88,12,0.45), inset 0 1px 0 rgba(255,255,255,0.45)" }
              : habilitado && isMaster
                ? { backgroundColor: ACCENT }
                : undefined
          }
        >
          {habilitado ? (
            <>
              {openLabel} <ArrowRight size={16} aria-hidden="true" />
            </>
          ) : (
            <>
              Próximamente <Lock size={14} aria-hidden="true" />
            </>
          )}
        </button>
      </div>

      {media && (
        <MediaAdminModal
          open={!!media}
          modo={media}
          tipo="programa"
          item={programa}
          onClose={() => setMedia(null)}
          onSaved={() => { setMedia(null); onMediaSaved?.(); }}
        />
      )}
      <VideoModal
        open={videoOpen}
        onClose={() => setVideoOpen(false)}
        title={programa.name}
        externalUrl={programa.video_demo_url || null}
        fileUrl={!programa.video_demo_url && programa.tiene_video_demo ? `${BACKEND}/api/software/programas/${programa.id}/video-demo` : null}
      />

      {/* PASO 2B · Selector compacto de plataforma para descargar */}
      {dlOpen && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
          data-testid={`programa-dl-modal-${programa.id}`}
          onClick={() => setDlOpen(false)}
        >
          <div
            className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Selecciona tu plataforma"
          >
            <div className="mb-4 flex items-center justify-between">
              <h4 className="text-sm font-extrabold text-slate-900">Selecciona tu plataforma</h4>
              <button onClick={() => setDlOpen(false)} aria-label="Cerrar" data-testid={`programa-dl-close-${programa.id}`} className="text-slate-400 hover:text-slate-700">
                <X size={16} />
              </button>
            </div>
            <div className="space-y-2.5">
              {platforms.map((p) => {
                const meta = [p.version ? `v${p.version}` : null, p.architecture || null, fmtSize(p.file_size)].filter(Boolean).join(" · ");
                return (
                  <div key={p.key} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 px-3 py-2.5">
                    <div className="min-w-0">
                      <span className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-800">
                        <p.Icon size={15} className="text-brand-blue" /> {p.label}
                      </span>
                      <p className="mt-0.5 truncate text-[11px] text-slate-500">{meta || (p.available ? "Disponible" : "Aún no disponible")}</p>
                    </div>
                    {p.available ? (
                      <a
                        href={p.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        {...(p.isUpload ? { download: true } : {})}
                        data-testid={`programa-dl-${p.key}-${programa.id}`}
                        className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-700"
                      >
                        <Download size={14} /> Descargar
                      </a>
                    ) : (
                      <span
                        data-testid={`programa-dl-${p.key}-soon-${programa.id}`}
                        className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-400"
                      >
                        <Lock size={13} /> {RELEASE_LABEL[p.release_status] || "Próximamente"}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
